import React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import get from 'lodash/fp/get';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { datadogLogs } from '@datadog/browser-logs';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';

import store from './main/store/configureStore';
import theme from './main/theme';
import App from './main/views/app/App';
import bugsnagEnabled from './utils/bugsnagEnv';
import StubLdRootProvider from './utils/launchDarklyStub';

const muiLicenseKey = process.env.REACT_APP_MUI_LICENSE_KEY;
const launchDarklyClientID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;
const hasLaunchDarklyClientId =
  Boolean(launchDarklyClientID) &&
  launchDarklyClientID !== 'undefined' &&
  launchDarklyClientID !== 'null';
const bugsnagKey = process.env.REACT_APP_BUGSNAG_KEY;
const datadogClientToken = process.env.REACT_APP_DATADOG_CLIENTTOKEN;
const datadogEnabled =
  Boolean(datadogClientToken) &&
  datadogClientToken !== 'undefined' &&
  datadogClientToken !== 'null';

if (bugsnagEnabled) {
  Bugsnag.start({
    apiKey: bugsnagKey,
    releaseStage: process.env.REACT_APP_ENV || 'local',
    plugins: [new BugsnagPluginReact()],
    onError: (event) => {
      const userData = get(['app', 'session', 'user'], store.getState());
      event.setUser(get('_id', userData), get('email', userData), get('name', userData));
    },
  });
}

if (datadogEnabled) {
  datadogLogs.init({
    clientToken: datadogClientToken,
    site: 'datadoghq.com',
    forwardErrorsToLogs: process.env.REACT_APP_ENV !== 'prod',
    sampleRate: 100,
    service: 'wae-web-client',
    env: process.env.REACT_APP_ENV || 'unknown',
  });
}

LicenseInfo.setLicenseKey(muiLicenseKey);
(async () => {
  const ErrorBoundary = bugsnagEnabled
    ? Bugsnag.getPlugin('react').createErrorBoundary(React)
    : ({ children }) => children;

  const LdRoot = hasLaunchDarklyClientId
    ? await asyncWithLDProvider({
        clientSideID: launchDarklyClientID,
        user: {
          key: 'wae-highbar-client',
          name: 'WAE Client',
        },
        options: {
          /* ... */
        },
      })
    : StubLdRootProvider;

  ReactDOM.render(
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Provider store={store}>
          <LdRoot>
            <DndProvider backend={HTML5Backend}>
              <App />
            </DndProvider>
          </LdRoot>
        </Provider>
      </ThemeProvider>
    </ErrorBoundary>,
    document.getElementById('root')
  );
})();
